import { Tag, Typography } from "antd"
import { type ColumnsType } from "antd/es/table"
import React from "react"
import { Link } from "react-router-dom"

import { empty } from "../../../constants/placeholders"
import { RoutesPaths } from "../../../constants/routes.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import {
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import {
  InvoiceRowActions,
  type InvoiceRowAction
} from "../helpers/invoice-row-actions.tsx"
import {
  INVOICE_ORIGINS,
  type InvoiceListItem
} from "../types/invoices.types.ts"

const { Text } = Typography

export type { InvoiceRowAction }

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetColumnsArgs {
  t: Translate
  editAllowed?: boolean
  voidAllowed?: boolean
  onSelectRow: (record: InvoiceListItem, action: InvoiceRowAction) => void
}

export const getColumns = (args: GetColumnsArgs): ColumnsType<InvoiceListItem> => {
  const { t, editAllowed, voidAllowed, onSelectRow } = args

  return [
    {
      title: t("invoices.columns.invoiceNumber"),
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 160,
      render: (text: string) => <Text strong>{text || empty}</Text>
    },
    {
      title: t("invoices.columns.project"),
      dataIndex: "project_info",
      key: "project",
      width: 220,
      ellipsis: true,
      render: (projectInfo: InvoiceListItem["project_info"]) =>
        projectInfo ? (
          <Link to={RoutesPaths.project.view(projectInfo.id).invoices}>
            {[projectInfo.uid, projectInfo.lead_name]
              .filter(Boolean)
              .join(" — ") || empty}
          </Link>
        ) : (
          empty
        )
    },
    {
      title: t("invoices.columns.origin"),
      dataIndex: "origin_display",
      key: "origin",
      width: 130,
      render: (label: string, record) => (
        <Tag
          color={record.origin === INVOICE_ORIGINS.GENERATED ? "blue" : "default"}
          className="m-0"
        >
          {label || empty}
        </Tag>
      )
    },
    {
      title: t("invoices.columns.customer"),
      dataIndex: "customer_name",
      key: "customer_name",
      width: 190,
      ellipsis: true,
      render: (name: string | null) => name || empty
    },
    {
      title: t("invoices.columns.dateIssued"),
      dataIndex: "date_issued",
      key: "date_issued",
      sorter: true,
      defaultSortOrder: "descend",
      width: 140,
      render: (value: string | null) => value || empty
    },
    {
      title: t("invoices.columns.dueDate"),
      dataIndex: "due_date",
      key: "due_date",
      sorter: true,
      width: 140,
      render: (value: string | null) => value || empty
    },
    {
      title: t("invoices.columns.amount"),
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      width: 140,
      render: (value: string) => <Text>{budgetFormat(value)}</Text>
    },
    {
      title: t("invoices.columns.paidToDate"),
      dataIndex: "paid_to_date",
      key: "paid_to_date",
      width: 140,
      render: (value: string) => <Text>{budgetFormat(value)}</Text>
    },
    {
      title: t("invoices.columns.status"),
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: InvoiceStatuses, record) => {
        const statusData = INVOICE_STATUSES_MAP?.[status]
        return (
          <Tag style={statusData?.style} className="m-0">
            {record?.status_display || statusData?.label || empty}
          </Tag>
        )
      }
    },
    {
      title: t("invoices.columns.actions"),
      key: "actions",
      width: 90,
      fixed: "right",
      className: "text-center",
      render: (_, record) => (
        <InvoiceRowActions
          record={record}
          t={t}
          editAllowed={editAllowed}
          voidAllowed={voidAllowed}
          onSelectRow={(action) => onSelectRow(record, action)}
        />
      )
    }
  ]
}
